import React, { useState, useContext } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../authProvider/authProvider";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { app, host } from "../../config";
import { v4 as uuidv4 } from "uuid";

import "./lossRatioSystem.css";
import { DataType } from "./lossRatioTableComponent";
import CircularProgress from "@mui/material/CircularProgress";
import LossRatioDataGrid from "./lossRatioTableComponent";

const auth = getAuth();
const db = getFirestore(app);

const LossRatioLookupSystem: React.FC = () => {
  const [groupNumber, setGroupNumber] = useState("");
  const [numberOfMonths, setNumberOfMonths] = useState("");
  const [groupId, setGroupId] = useState("");
  const [planId, setPlanId] = useState("");
  const [apiData, setApiData] = useState<Array<DataType>>([]);
  const [showApiData, setShowApiData] = useState(false);
  const [isVBA, setIsVBA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterType, setFilterType] = useState<"contractYear" | "numberOfMonths">("contractYear");

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser, suspended, role, selectedGroup } = authContext;

  const navigate = useNavigate();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  var tk: string;
  currentUser
    ?.getIdToken(false)
    .then((idToken: string) => {
      tk = idToken;
    })
    .catch((error: any) => {
      console.log(error);
    });

  if (suspended == true) {
    signOut(auth)
      .then(() => {
        console.log("Signed out");
        return <Navigate to="/login" />;
      })
      .catch((error) => {
        console.error("Error signing out", error);
      });
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(false);
    setShowApiData(true);

    if (filterType === "numberOfMonths" && !numberOfMonths) {
      alert("Please enter both start and end months for the range!");
      return;
    }

    let holder: { [key: string]: any } = {
      groupId: groupId,
      planId: planId,
      filterType: filterType,
    };

    if (filterType === "numberOfMonths") {
      holder.numberOfMonths = numberOfMonths;
    }

    setIsLoading(true);

    let params = new URLSearchParams(holder);

    fetch(`${host}/api/upload/lossRatioReport?${params}`, {
      method: "GET",
      headers: {
        authorization: `Bearer ${tk}`,
      },
    })
      .then(async (response: Response) => {
        if (!response.ok) {
          await response.text().then((text) => {
            console.log(text);
            alert(`An error has occured with your query:\n${text}`);
          });
          return;
        } else {
          await response.json().then((res) => {
            setIsLoading(false);
            setIsVBA(res.vba);
            setShowApiData(true);
            const dataWithUidAndFormattedDates = res.results.map((item) => {
              if (item.Coverage_End) {
                console.log();
                item.Coverage_End = new Date(item.Coverage_End).toLocaleDateString("en-US", { timeZone: "UTC" });
              }
              if (item.Coverage_Start) {
                item.Coverage_Start = new Date(item.Coverage_Start).toLocaleDateString("en-US", { timeZone: "UTC" });
              }
              return {
                ...item,
                uid: uuidv4(),
              };
            });
            setApiData(dataWithUidAndFormattedDates);
          });
        }
      })
      .catch((error: any) => {
        console.log(error);
        alert("An error has occured when searching for LossRatio");
      })
      .finally(() => {
        setIsLoading(false);
        console.log("Processing finished");
      });
  };

  const goBack = () => {
    setShowApiData(false);
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showApiData) {
    return (
      <div className="App">
        <header className="app-header">
          <div className="header">
            <h1>LossRatio</h1>
          </div>
          <div className="button-container2">
            <button className="LossRatio-back-button" onClick={goBack}>
              Back
            </button>
          </div>
        </header>
        {isVBA && tk ? (
          <>
            <LossRatioDataGrid requestType={filterType} data={apiData} />
          </>
        ) : (
          <>
            <LossRatioDataGrid requestType={filterType} data={apiData} />
          </>
        )}
      </div>
    );
  } else {
    return (
      <div>
        <div className="header">
          <h1>Loss Ratio Table</h1>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="filter-section">
            <h3>Select Filter Type</h3>
            <div className="filter-type-selection">
              <div className="radio-button-wrapper">
                <input
                  type="radio"
                  id="contractYear"
                  value="contractYear"
                  checked={filterType === "contractYear"}
                  onChange={(e) => setFilterType(e.target.value as "contractYear" | "numberOfMonths")}
                />
                <label htmlFor="contractYear">Contract Year</label>
              </div>
              <div className="radio-button-wrapper">
                <input
                  type="radio"
                  id="numberOfMonths"
                  value="numberOfMonths"
                  checked={filterType === "numberOfMonths"}
                  onChange={(e) => setFilterType(e.target.value as "contractYear" | "numberOfMonths")}
                />
                <label htmlFor="numberOfMonths">Month Range</label>
              </div>
            </div>

            <div className="filter-description">
              {filterType === "contractYear" ? (
                <p className="description-text">
                  Displays the premiums billed and claims paid for given contract period. Ratios are calculated based on
                  the months billed and claims with a date of service that fall within the contract year.
                </p>
              ) : (
                <p className="description-text">
                  Displays the premiums collected and claims paid during the monthly range selected. Ratios are
                  calculated based on collected premiums and claims with a paid date that fall within the selected
                  months.
                </p>
              )}
            </div>

            {filterType === "contractYear" ? null : (
              <div className="filter-inputs">
                <div className="month-range-inputs">
                  <div>
                    <label htmlFor="numberOfMonths">Number Of Months to View</label>
                    <input
                      id="numberOfMonths"
                      type="number"
                      value={numberOfMonths}
                      onChange={(e) => setNumberOfMonths(e.target.value)}
                      min="1"
                      max="12"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="optional-ids">
            <label>
              Group ID: (Optional)
              <input type="text" value={groupId} onChange={(e) => setGroupId(e.target.value)} />
            </label>
            <label>
              Plan ID: (Optional)
              <input type="text" value={planId} onChange={(e) => setPlanId(e.target.value)} />
            </label>
          </div>

          <button type="submit">Look up</button>
        </form>
      </div>
    );
  }
};

export default LossRatioLookupSystem;
